import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle network errors
    if (!error.response) {
      console.error('Network error:', error.message);
      return Promise.reject(error);
    }

    // Handle authentication errors
    if (error.response?.status === 401) {
      // Only redirect if we're not already on the login page
      if (window.location.pathname !== '/login') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (profileData) => api.put('/auth/profile', profileData),
  changePassword: (passwordData) => api.put('/auth/change-password', passwordData),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  verifyResetToken: (token) => api.post('/auth/verify-reset-token', { token }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, newPassword }),
  verifyToken: () => api.get('/auth/verify'),
};

// Customer API
export const customerAPI = {
  getAll: (params) => api.get('/customers', { params }),
  getById: (id) => api.get(`/customers/${id}`),
  create: (customerData) => api.post('/customers', customerData),
  update: (id, customerData) => api.put(`/customers/${id}`, customerData),
  delete: (id) => api.delete(`/customers/${id}`),
  search: (query, params) => api.get(`/customers/search/${query}`, { params }),
};

// Virtual Bank Account Balance API (moved from customer balance)
export const customerBalanceAPI = {
  getAll: () => api.get('/virtual-bank-accounts/balances'),
  syncAll: () => api.post('/virtual-bank-accounts/sync-all'),
  syncCustomer: (accountId) => api.post(`/virtual-bank-accounts/${accountId}/sync`),
};


export const virtualBankAccountAPI = {
  getAll: (params) => api.get('/virtual-bank-accounts', { params }),
  getAllComprehensive: () => api.get('/virtual-bank-accounts/all'),
  createCustomerAndAccount: (customerData) => api.post('/virtual-bank-accounts/create-customer-and-account', customerData),
  getByCustomer: (customerId) => api.get(`/virtual-bank-accounts/customer/${customerId}`),
  simulatePayment: (accountId, amount) => api.post(`/virtual-bank-accounts/${accountId}/simulate-payment`, { amount }),
  getCashBalanceTransactions: (customerId, params) => api.get(`/virtual-bank-accounts/customer/${customerId}/transactions`, { params }),
  getFundingEvents: (customerId, params = {}) => 
    api.get(`/virtual-bank-accounts/customer/${customerId}/funding-events`, { params }),
  updateNickname: (accountId, nickname) => api.put(`/virtual-bank-accounts/${accountId}/nickname`, { nickname }),
  initiatePayout: (accountId, amount) => api.post(`/virtual-bank-accounts/${accountId}/payout`, { amount }),
  // Balance sync methods
  getBalances: () => api.get('/virtual-bank-accounts/balances'),
  syncAll: () => api.post('/virtual-bank-accounts/sync-all'),
  syncAccount: (accountId) => api.post(`/virtual-bank-accounts/${accountId}/sync`),
};





// Payment Intent API
export const paymentIntentAPI = {
  getAll: (params = {}) => api.get('/payment-intents', { params }),
  getById: (id) => api.get(`/payment-intents/${id}`),
  create: (data) => api.post('/payment-intents/create', data),
  confirm: (id) => api.post(`/payment-intents/${id}/confirm`),
  cancel: (id) => api.post(`/payment-intents/${id}/cancel`),
  searchCustomers: (query) => api.get('/payment-intents/customers/search', { params: { q: query } })
};

// Export the api instance as both named and default export
export { api };
export default api;